// XAI Examples for all 12 platforms (3 examples each)
// For DEMO purposes - structured as per the XAI framework

export const xaiExamples = {
  // Platform 1: Acoustic AI
  acoustic: {
    title: "Acoustic AI - Respiratory Analysis",
    icon: "🎙️",
    examples: [
      {
        id: "acoustic_001",
        case: "COPD Exacerbation - Acute Respiratory Failure",
        prediction: { label: "Severe COPD Exacerbation", confidence: 0.94, severity: "HIGH" },
        evidence_layers: {
          acoustic: [
            { feature_id: "wheezing_500hz", description: "Polyphonic wheezing at 500-800 Hz (expiratory) [Lower airways]", weight: 0.42 },
            { feature_id: "prolonged_expiration", description: "Expiratory:Inspiratory ratio = 4.2:1 (normal <2:1) [Air trapping]", weight: 0.35 },
            { feature_id: "fine_crackles", description: "Fine crackles 200-400 Hz in bilateral lower lobes [End-expiration]", weight: 0.23 }
          ],
          clinical: [
            { feature_id: "spO2_89", description: "SpO₂ 89% on room air (hypoxia) [Normal: >94%]", weight: 0.38 },
            { feature_id: "resp_rate_24", description: "Respiratory rate 24/min (tachypnea) [Normal: 12-20/min]", weight: 0.28 },
            { feature_id: "dyspnea_nyha", description: "Dyspnea at rest (NYHA Class III)", weight: 0.22 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Wheezing 500-800 Hz → Dynamic Airway Narrowing", mechanism: "Polyphonic wheezing at 500-800 Hz indicates turbulent airflow through multiple narrowed lower airways. Smooth muscle contraction and mucosal edema cause dynamic airway collapse during expiration." },
          { physiological_link: "Prolonged Expiration (E:I 4.2:1) → Air Trapping & Hyperinflation", mechanism: "Expiratory:Inspiratory ratio of 4.2:1 reflects severe airway obstruction with delayed emptying. This causes dynamic hyperinflation, increasing work of breathing and impairing gas exchange." },
          { physiological_link: "SpO₂ 89% → Ventilation-Perfusion Mismatch", mechanism: "Hypoxia (SpO₂ 89%) confirms ventilation-perfusion (V/Q) mismatch due to alveolar collapse and airway obstruction. This indicates acute respiratory compromise requiring oxygen supplementation." }
        ],
        clinical_explanation: "The CTF model diagnoses severe COPD exacerbation by integrating multi-layer acoustic evidence (polyphonic wheezing, prolonged expiration, crackles) with clinical hypoxia (SpO₂ 89%) and tachypnea. The wheezing frequency (500-800 Hz) is pathognomonic for lower airway obstruction, while the E:I ratio of 4.2:1 confirms severe air trapping. Combined with hypoxia, this indicates acute respiratory failure requiring immediate bronchodilator therapy, oxygen supplementation, and consideration of systemic corticosteroids.",
        recommended_actions: [
          "Start nebulized bronchodilators: Albuterol 5mg + Ipratropium 0.5mg q4h (IMMEDIATE)",
          "Administer supplemental oxygen: Target SpO₂ 92-96% via nasal cannula 2-4 L/min (IMMEDIATE)",
          "Systemic corticosteroids: Prednisone 40mg PO daily for 5 days (Within 1 hour)",
          "Monitor respiratory status: RR and SpO₂ every 2 hours (Continuous)",
          "Consider ABG if SpO₂ <90% despite oxygen (Within 2 hours)"
        ]
      },
      {
        id: "acoustic_002",
        case: "Bacterial Pneumonia - Right Lower Lobe",
        prediction: { label: "Bacterial Pneumonia (RLL)", confidence: 0.89, severity: "MODERATE" },
        evidence_layers: {
          acoustic: [
            { feature_id: "coarse_crackles_rll", description: "Coarse crackles 150-300 Hz in right lower lobe (Mid-to-late inspiration)", weight: 0.45 },
            { feature_id: "bronchial_breath_sounds", description: "Bronchial breath sounds over RLL [Consolidation marker]", weight: 0.32 },
            { feature_id: "egophony", description: "Egophony present on RLL auscultation [Alveolar consolidation]", weight: 0.23 }
          ],
          clinical: [
            { feature_id: "fever_38.5c", description: "Fever 38.5°C with chills", weight: 0.40 },
            { feature_id: "productive_cough", description: "Productive cough with rust-colored purulent sputum", weight: 0.35 },
            { feature_id: "pleuritic_pain", description: "Right-sided pleuritic chest pain", weight: 0.25 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Coarse Crackles → Alveolar Fluid from Inflammatory Exudate", mechanism: "Coarse crackles at 150-300 Hz indicate fluid-filled alveoli from bacterial inflammatory exudate. The late inspiratory timing and RLL location are classic for bacterial pneumonia." },
          { physiological_link: "Bronchial Breath Sounds → Lung Consolidation", mechanism: "Bronchial breath sounds over RLL indicate consolidation where sound transmits through solid (fluid-filled) tissue rather than air-filled lung. This is pathognomonic for lobar pneumonia." },
          { physiological_link: "Fever 38.5°C + Purulent Sputum → Systemic Bacterial Infection", mechanism: "Fever with chills and purulent (rust-colored) sputum confirms systemic inflammatory response to bacterial infection. Rust-colored sputum is classic for Strep. pneumoniae." }
        ],
        clinical_explanation: "The model diagnoses right lower lobe bacterial pneumonia by integrating acoustic evidence of coarse crackles, bronchial breath sounds, and egophony with clinical fever (38.5°C) and purulent sputum. The coarse crackles (150-300 Hz) indicate alveolar fluid from inflammatory exudate, while bronchial breath sounds confirm consolidation. Rust-colored sputum is pathognomonic for Strep. pneumoniae. This requires empiric antibiotics covering typical bacterial pathogens.",
        recommended_actions: [
          "Start empiric antibiotics: Ceftriaxone 1g IV + Azithromycin 500mg PO (Within 1 hour)",
          "Obtain cultures: Sputum culture + 2 blood cultures (Before antibiotics)",
          "Chest X-ray: Confirm RLL consolidation + assess for complications (Within 2 hours)",
          "Monitor temperature curve: Temperature every 4 hours (Continuous)",
          "Consider CURB-65 score calculation (Immediate)"
        ]
      },
      {
        id: "acoustic_003",
        case: "Acute Asthma Exacerbation - Moderate Severity",
        prediction: { label: "Acute Asthma Exacerbation", confidence: 0.91, severity: "MODERATE" },
        evidence_layers: {
          acoustic: [
            { feature_id: "polyphonic_wheezing_expiratory", description: "Polyphonic wheezing throughout expiratory phase (400-1000 Hz) [Bilateral diffuse]", weight: 0.48 },
            { feature_id: "prolonged_expiratory_phase", description: "Expiratory phase = 5.2 seconds (normal 2-3s) [E:I Ratio 3.5:1]", weight: 0.35 },
            { feature_id: "diminished_breath_sounds", description: "Diminished breath sounds [Warning: Silent chest concern]", weight: 0.17 }
          ],
          clinical: [
            { feature_id: "peak_flow_45_percent", description: "Peak flow 45% of predicted (450 mL vs 1000 mL predicted) [Moderate-severe]", weight: 0.42 },
            { feature_id: "dyspnea_at_rest", description: "Dyspnea at rest, unable to complete sentences", weight: 0.30 },
            { feature_id: "use_of_accessory_muscles", description: "Visible use of accessory muscles for breathing", weight: 0.28 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Polyphonic Wheezing → Diffuse Bronchoconstriction", mechanism: "Polyphonic wheezing across 400-1000 Hz indicates diffuse bronchoconstriction affecting multiple airway sizes simultaneously. This is the hallmark of acute asthma, where IgE-mediated inflammation causes smooth muscle contraction." },
          { physiological_link: "Prolonged Expiration (E:I 3.5:1) → Reversible Airflow Limitation", mechanism: "Expiratory phase of 5.2 seconds (E:I 3.5:1) reflects increased airway resistance from bronchoconstriction. In asthma, this is reversible with bronchodilators." },
          { physiological_link: "Peak Flow 45% → Severe Airflow Limitation", mechanism: "Peak flow at 45% of predicted confirms severe airflow limitation requiring immediate intervention. This falls in the 'moderate-severe' exacerbation category, warranting ED evaluation and systemic corticosteroids." }
        ],
        clinical_explanation: "The model diagnoses acute asthma exacerbation by integrating polyphonic wheezing (diffuse bronchoconstriction), prolonged expiration (airflow limitation), and peak flow reduction (45% predicted) with dyspnea at rest. The polyphonic nature (400-1000 Hz) indicates diffuse involvement of multiple airway sizes, classic for asthma rather than fixed obstruction. The diminished breath sounds warrant concern for 'silent chest' (near-complete obstruction). This is a moderate-severe exacerbation requiring immediate bronchodilators, systemic corticosteroids, and ED evaluation.",
        recommended_actions: [
          "Administer nebulized albuterol: Albuterol 5mg + Ipratropium 0.5mg q20min × 3 (IMMEDIATE)",
          "Systemic corticosteroids: Prednisone 50mg PO daily for 5-7 days (Within 1 hour)",
          "Monitor peak flow: Peak flow every 30 min after treatment (Continuous)",
          "Evaluate for oxygen if SpO₂ <92% or peak flow <40% (Immediate assessment)",
          "ED transfer: Peak flow 45% needs emergency monitoring (Within 30 min)"
        ]
      }
    ]
  },

  // Platform 2: Radiology AI
  radiology: {
    title: "Radiology AI - Chest Imaging",
    icon: "🩻",
    examples: [
      {
        id: "rad_001",
        case: "Non-Small Cell Lung Cancer (NSCLC) - Stage IIIA",
        prediction: { label: "Stage IIIA NSCLC", confidence: 0.92, severity: "HIGH" },
        evidence_layers: {
          radiology: [
            { feature_id: "spiculated_mass", description: "4.2 cm spiculated mass in Right Upper Lobe (RUL)", weight: 0.45 },
            { feature_id: "mediastinal_adenopathy", description: "Mediastinal lymphadenopathy [N2 node involvement]", weight: 0.32 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Spiculated Mass → Malignant Invasion", mechanism: "Spiculated margins reflect infiltrative tumor growth along pulmonary septae, highly characteristic of primary lung malignancies." }
        ],
        clinical_explanation: "The model diagnoses Stage IIIA non-small cell lung cancer based on structural imaging features showing structural mass infiltration and local lymph node expansion.",
        recommended_actions: [
          "Refer to thoracic oncology multidisciplinary team",
          "Obtain tissue biopsy for histological confirmation"
        ]
      }
    ]
  },

  // Platform 3: Pathology AI
  pathology: {
    title: "Pathology AI - Histopathology",
    icon: "🔬",
    examples: [
      {
        id: "path_001",
        case: "Lung Cancer Differentiation",
        prediction: { label: "Squamous Cell Carcinoma", confidence: 0.94, severity: "HIGH" },
        evidence_layers: {
          pathology: [
            { feature_id: "keratin_pearls", description: "Keratin pearls present in cellular architecture", weight: 0.42 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Keratin Pearls → Squamous Differentiation", mechanism: "Keratin pearls indicate advanced squamous cell maturation, distinguishing it from adenocarcinoma formats." }
        ],
        clinical_explanation: "The model diagnoses squamous cell carcinoma based on cellular and structural pathological evidence.",
        recommended_actions: [
          "Confirm with targeted immunohistochemistry arrays",
          "Complete baseline patient staging with CT/PET-CT"
        ]
      }
    ]
  },

  // Platform 4: Clinical Lab
  clinical_lab: {
    title: "Clinical Diagnostics Engine",
    icon: "📊",
    examples: [
      {
        id: "cl_001",
        case: "Pneumonia Diagnostics Matrix",
        prediction: { label: "Community-Acquired Pneumonia", confidence: 0.93, severity: "MODERATE" },
        evidence_layers: {
          clinical: [
            { feature_id: "fever", description: "Fever 39°C accompanied by active chills", weight: 0.40 }
          ],
          laboratory: [
            { feature_id: "wbc", description: "WBC 18,000 cells/µL (indicates leukocytosis)", weight: 0.38 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Fever + Leukocytosis → Acute Infection", mechanism: "Fever combined with highly elevated white blood cell counts indicates acute systemic immune response to bacterial infection." }
        ],
        clinical_explanation: "The model diagnoses community-acquired pneumonia using an ensemble mapping of clinical vitals and lab metrics.",
        recommended_actions: [
          "Start targeted empiric antibiotic therapies",
          "Obtain sputum culture for micro evaluation"
        ]
      }
    ]
  },

  // Platform 5: Acoustic + Clinical
  acoustic_clinical: {
    title: "Acoustic + Clinical Data",
    icon: "🎙️📊",
    examples: [
      {
        id: "ac_cl_001",
        case: "COPD Exacerbation",
        prediction: { label: "Acute COPD", confidence: 0.90, severity: "MODERATE" },
        evidence_layers: {
          acoustic: [
            { feature_id: "wheezing", description: "Expiratory wheezing markers caught on microphone array", weight: 0.42 }
          ],
          clinical: [
            { feature_id: "dyspnea", description: "Active dyspnea reports during baseline examination", weight: 0.38 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Wheezing + Dyspnea → Airway Obstruction", mechanism: "Combined acoustic markers and physical strain values confirm localized airway narrowing issues." }
        ],
        clinical_explanation: "The model identifies acute COPD profiles by tracking real-time soundscapes alongside incoming patient triage profiles.",
        recommended_actions: [
          "Initiate rapid bronchodilator treatments",
          "Continuously log peripheral SpO₂ parameters"
        ]
      }
    ]
  },

  // Platform 6: Radiology + Clinical
  radiology_clinical: {
    title: "Radiology + Clinical Data",
    icon: "🩻📊",
    examples: [
      {
        id: "rad_cl_001",
        case: "Pneumonia",
        prediction: { label: "Bacterial Pneumonia", confidence: 0.93, severity: "HIGH" },
        evidence_layers: {
          radiology: [
            { feature_id: "consolidation", description: "Dense lobar consolidation observed on chest plate", weight: 0.45 }
          ],
          clinical: [
            { feature_id: "fever", description: "Sustained high-grade fever parameters (>39°C)", weight: 0.38 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Consolidation + Fever → Lobar Infection", mechanism: "Structural air space displacement matching high body temperature spikes strongly highlights active bacterial processes." }
        ],
        clinical_explanation: "The framework registers bacterial pneumonia flags via high-confidence validation between radiography and systemic biomarkers.",
        recommended_actions: [
          "Administer initial antibiotic regimens promptly",
          "Order deep respiratory culture assays"
        ]
      }
    ]
  },

  // Platform 7: Radiology + Pathology Fusion
  fusion_rad_path: {
    title: "Radiology + Pathology Fusion",
    icon: "🧬",
    examples: [
      {
        id: "rp_001",
        case: "Lung Cancer Staging",
        prediction: { label: "Stage IIIB NSCLC", confidence: 0.95, severity: "HIGH" },
        evidence_layers: {
          radiology: [
            { feature_id: "mass", description: "5.5 cm structural mass identified near the hilum", weight: 0.40 }
          ],
          pathology: [
            { feature_id: "cytokeratin", description: "Cytokeratin 7 marker positive via histological staging", weight: 0.35 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Mass + Cytokeratin → Epithelial Malignancy", mechanism: "Macroscopic tumor volume matched with molecular markers confirms advanced carcinoma paths." }
        ],
        clinical_explanation: "The system provides an integrated diagnostic tracking profile for Stage IIIB structures by analyzing gross imaging patterns alongside micro-staining properties.",
        recommended_actions: [
          "Convene an immediate multidisciplinary tumor board review",
          "Map staging schemas for chemoradiotherapy inclusion"
        ]
      }
    ]
  },

  // Platform 8: Radiology + Acoustic Fusion
  fusion_rad_acoustic: {
    title: "Radiology + Acoustic Fusion",
    icon: "🎧",
    examples: [
      {
        id: "ra_001",
        case: "Pneumonia Assessment",
        prediction: { label: "Severe Pneumonia", confidence: 0.93, severity: "HIGH" },
        evidence_layers: {
          radiology: [
            { feature_id: "consolidation", description: "Bilateral consolidation zones on lung parenchyma", weight: 0.42 }
          ],
          acoustic: [
            { feature_id: "crackles", description: "Widespread coarse crackles captured symmetrically", weight: 0.38 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Consolidation + Crackles → Alveolar Fluid Collection", mechanism: "X-ray opacity combined with crackle patterns details significant fluid accumulation in alveolar boundaries." }
        ],
        clinical_explanation: "The engine registers advanced pneumonia classifications via dual-channel spatial mapping and acoustic tracking streams.",
        recommended_actions: [
          "Begin broad-spectrum intravenous antibiotic courses",
          "Assess triage guidelines for intermediate or ICU step-down admission"
        ]
      }
    ]
  },

  // Platform 9: Pathology + Acoustic Fusion
  fusion_path_acoustic: {
    title: "Pathology + Acoustic Fusion",
    icon: "🎤",
    examples: [
      {
        id: "pa_001",
        case: "Lung Cancer Pathway",
        prediction: { label: "NSCLC Advanced", confidence: 0.92, severity: "HIGH" },
        evidence_layers: {
          pathology: [
            { feature_id: "carcinoma", description: "Invasive poorly differentiated carcinoma tracks on slide", weight: 0.45 }
          ],
          acoustic: [
            { feature_id: "stridor", description: "Inspiratory stridor frequencies recorded over trachea", weight: 0.30 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Carcinoma + Stridor → Upper Airway Occlusion", mechanism: "Microscopic aggressive malignancy patterns intersecting with mechanical sound features signal real-time structural airway obstruction." }
        ],
        clinical_explanation: "The model flags locally advanced variants by tracking underlying cellular profiles against severe mechanical sound obstructions.",
        recommended_actions: [
          "Initiate hyper-urgent clinical oncology management pipelines",
          "Schedule urgent bronchoscopy to assess central airway patency"
        ]
      }
    ]
  },

  // Platform 10: Triple Fusion Core
  fusion_rad_acoustic_clinical: {
    title: "Triple Fusion Core",
    icon: "🩻🎙️📊",
    examples: [
      {
        id: "rac_001",
        case: "Severe Pneumonia Matrix",
        prediction: { label: "Severe Pneumonia", confidence: 0.96, severity: "CRITICAL" },
        evidence_layers: {
          radiology: [
            { feature_id: "consolidation", description: "Dense bilateral multi-lobar consolidation signatures", weight: 0.40 }
          ],
          acoustic: [
            { feature_id: "crackles", description: "Widespread bubbling coarse crackles across multiple lung fields", weight: 0.35 }
          ],
          clinical: [
            { feature_id: "fever", description: "Hyperpyrexia conditions identified (>39.5°C)", weight: 0.30 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "All Modalities → Fulminant Infection Status", mechanism: "Simultaneous spatial, auditory, and systemic marker cross-checking points to severe consolidative infection profiles." }
        ],
        clinical_explanation: "The triple-core infrastructure establishes critical pneumonia statuses via full real-time validation across separate anatomical sensing frameworks.",
        recommended_actions: [
          "Start immediate broad-spectrum empiric IV antibiotic therapies",
          "Enforce continuous pulse-oximetry and mechanical tracking loops"
        ]
      }
    ]
  },

  // Platform 11: Quad Fusion Architecture
  fusion_all_except_omics: {
    title: "Quad Fusion Architecture",
    icon: "🌐🫁",
    examples: [
      {
        id: "quad_001",
        case: "Advanced Lung Cancer Metastasis",
        prediction: { label: "Stage IV NSCLC", confidence: 0.94, severity: "CRITICAL" },
        evidence_layers: {
          radiology: [
            { feature_id: "mass", description: "Metastatic lung masses identified across bilateral fields", weight: 0.30 }
          ],
          pathology: [
            { feature_id: "cytokeratin", description: "Cytokeratin positive structure signatures on tissue core", weight: 0.28 }
          ],
          acoustic: [
            { feature_id: "stridor", description: "Tracheal airway stridor profiles recorded during quiet breathing", weight: 0.25 }
          ],
          clinical: [
            { feature_id: "weight_loss", description: "Rapid unexpected systemic weight loss (>10% mass drop)", weight: 0.22 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "Multi-Modality Matrix → Advanced Cachectic Carcinoma", mechanism: "Combining micro-tissue, acoustic strain, macro-imaging, and host systemic indicators exposes complex advanced disease dynamics." }
        ],
        clinical_explanation: "The quad-engine framework isolates Stage IV malignant trends by concurrently registering structural, cellular, soundscape, and system-level physiological strain vectors.",
        recommended_actions: [
          "Coordinate urgent priority palliative and oncology interventions",
          "Organize targeted whole-body staging profiles via PET-CT setups"
        ]
      }
    ]
  },

  // Platform 12: Molecular Omic Fusion
  fusion_molecular_omic: {
    title: "Molecular Omic Fusion",
    icon: "🔬🧬📊",
    examples: [
      {
        id: "omic_001",
        case: "Targetable Genotypic NSCLC",
        prediction: { label: "EGFR+ NSCLC Variant", confidence: 0.93, severity: "HIGH" },
        evidence_layers: {
          pathology: [
            { feature_id: "adenocarcinoma", description: "Primary lung adenocarcinoma cell formations detected", weight: 0.35 }
          ],
          clinical: [
            { feature_id: "non_smoker", description: "Demographic profile match: Lifelong non-smoker background", weight: 0.30 }
          ],
          omics: [
            { feature_id: "egfr_mutation", description: "EGFR Tyrosine Kinase Domain Exon 19 deletion identified", weight: 0.40 }
          ]
        },
        reasoning_bridge: [
          { physiological_link: "EGFR Deletion + Adenocarcinoma Architecture → High Actionability", mechanism: "Specific genomic pathway alterations inside adenocarcinoma tissue models outline highly predictable responsiveness criteria for modern therapeutic interventions." }
        ],
        clinical_explanation: "The omic processing pipeline resolves specific drug-targetable mutation states by analyzing cellular slides alongside deep host sequencing reads.",
        recommended_actions: [
          "Initiate first-line tailored EGFR Tyrosine Kinase Inhibitor (TKI) regimens",
          "Configure specific circulating tumor DNA checkpoints to monitor response fields"
        ]
      }
    ]
  }
};
